import React, { useEffect, useRef, useState } from 'react';
import { IonButton, IonCard, IonCardContent } from '@ionic/react';
import { useHistory } from 'react-router-dom';
import { requestService, whatsappMetaSendPreferences } from '../../../../../Utils/Services';
import './WhatsAppSendPreferences.css';

type Mode = 'text' | 'template';
type Preferences = { orders: Mode; recovery: Mode };
type Response = { preferences: Preferences; canEdit: boolean };
const valid = (data: any): data is Response => !!data?.preferences && ['text', 'template'].includes(data.preferences.orders)
  && ['text', 'template'].includes(data.preferences.recovery) && typeof data.canEdit === 'boolean';

const WhatsAppSendPreferences: React.FC<{ empresaId: string }> = ({ empresaId }) => {
  const history = useHistory();
  const [data, setData] = useState<Response | null>(null);
  const [busy, setBusy] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [reload, setReload] = useState(0);
  const generation = useRef(0);
  const saving = useRef(false);
  const url = whatsappMetaSendPreferences.url.replace('{empresaId}', encodeURIComponent(empresaId));
  useEffect(() => {
    const current = ++generation.current;
    setData(null); setError(''); setSuccess(''); setAccepted(false); setBusy(false); saving.current = false;
    requestService(url, { method: 'GET', credentials: 'include' }, history, (result: unknown) => {
      if (current !== generation.current) return;
      if (!valid(result)) { setError('Não foi possível carregar as preferências.'); return; }
      setData(result);
    }, () => { if (current === generation.current) setError('Não foi possível carregar as preferências.'); }, true);
    return () => { generation.current += 1; };
  }, [url, history, reload]);
  const usesTemplates = data?.preferences.orders === 'template' || data?.preferences.recovery === 'template';
  const save = () => {
    if (!data?.canEdit || saving.current || (usesTemplates && !accepted)) return;
    saving.current = true; setBusy(true); setError(''); setSuccess('');
    const current = generation.current;
    requestService(url, { method: 'POST', credentials: 'include', body: JSON.stringify({ ...data.preferences, acceptCharges: accepted }) }, history,
      (result: unknown) => {
        if (current !== generation.current) return;
        saving.current = false; setBusy(false);
        if (!valid(result)) { setError('Resposta inválida. Recarregue para confirmar o que foi salvo.'); return; }
        setData(result); setSuccess('Preferências de envio salvas.');
      }, () => {
        if (current !== generation.current) return;
        saving.current = false; setBusy(false); setError('Não foi possível confirmar o salvamento. Recarregue e confira as preferências.');
      }, true);
  };
  const change = (field: keyof Preferences, value: string) => {
    if (!data || (value !== 'text' && value !== 'template')) return;
    setData({ ...data, preferences: { ...data.preferences, [field]: value } });
    setAccepted(false); setSuccess('');
  };
  return <IonCard className="integracoes-card wa-send-preferences"><IonCardContent>
    <h2>Formato dos envios automáticos</h2>
    <p>Escolha separadamente para o WhatsApp Oficial. Não há troca automática de formato se a Meta recusar uma mensagem.</p>
    {!data && !error && <p role="status">Carregando preferências…</p>}
    {error && <p role="alert">{error}</p>}
    {success && <p role="status">{success}</p>}
    {data && <>
      <label>Avisos de pedidos
        <select aria-label="Formato dos avisos de pedidos" value={data.preferences.orders} disabled={!data.canEdit || busy}
          onChange={event => change('orders', event.target.value)}>
          <option value="text">Texto livre (padrão)</option><option value="template">Templates aprovados — Utility</option>
        </select>
      </label>
      <label>Recuperação de venda
        <select aria-label="Formato da recuperação de venda" value={data.preferences.recovery} disabled={!data.canEdit || busy}
          onChange={event => change('recovery', event.target.value)}>
          <option value="text">Texto livre (padrão)</option><option value="template">Templates aprovados — Marketing</option>
        </select>
      </label>
      <p>Texto livre pode ser recusado pelas regras da Meta. Templates podem gerar cobrança e precisam estar aprovados. Nenhuma opção garante entrega ou gratuidade permanente.</p>
      <p>Esta escolha não ativa a recuperação de venda nem altera seus filtros. Atendimento do robô, campanhas e WhatsApp Web permanecem com suas regras atuais.</p>
      {usesTemplates && <label className="wa-send-consent"><input type="checkbox" checked={accepted} disabled={!data.canEdit || busy}
        onChange={event => setAccepted(event.target.checked)} />
        Aceito as possíveis cobranças da Meta nos envios por template, inclusive Marketing na recuperação de venda.
      </label>}
      {!data.canEdit && <p>Você tem acesso somente para leitura.</p>}
      <IonButton onClick={save} disabled={!data.canEdit || busy || (!!usesTemplates && !accepted)}>
        {busy ? 'Salvando…' : 'Salvar preferências de envio'}
      </IonButton>
    </>}
    {error && <IonButton fill="outline" disabled={busy} onClick={() => setReload(value => value + 1)}>Recarregar preferências</IonButton>}
  </IonCardContent></IonCard>;
};
export default WhatsAppSendPreferences;
